import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError } from "../lib/api";
import { useUnits, CREAT_SI_TO_US } from "../context/UnitsContext";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function LabForm({ catId, onAdded }) {
  const { units } = useUnits();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today,
    creatinine: "",
    sdma: "",
    usg: "",
    upc: "",
    bp: "",
    notes: "",
  });

  const reset = () =>
    setForm({ date: today, creatinine: "", sdma: "", usg: "", upc: "", bp: "", notes: "" });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const num = (v) => (v === "" || v == null ? null : parseFloat(v));
    let creat_mg = num(form.creatinine);
    if (creat_mg != null && units === "SI") {
      creat_mg = creat_mg * CREAT_SI_TO_US;
    }
    try {
      await api.post(`/cats/${catId}/labs`, {
        date: form.date,
        creatinine_mg_dl: creat_mg,
        sdma_ug_dl: num(form.sdma),
        usg: num(form.usg),
        upc: num(form.upc),
        bp_systolic: form.bp === "" ? null : parseInt(form.bp, 10),
        bp_method: form.bp_method || null,
        bp_cuff_position: form.bp_cuff || null,
        bp_stress: form.bp_stress || null,
        bp_n_readings: form.bp_n === "" ? null : parseInt(form.bp_n, 10),
        notes: form.notes || null,
      });
      toast.success("Lab result added");
      setOpen(false);
      reset();
      onAdded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const creatUnit = units === "SI" ? "µmol/L" : "mg/dL";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-lab-button" className="rounded-full">
          <Plus size={16} className="mr-1.5" /> Add lab result
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="add-lab-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">New lab result</DialogTitle>
          <DialogDescription>
            Enter what you have — blank fields are okay. Creatinine and SDMA drive staging.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="lab-date">Date</Label>
            <Input
              id="lab-date"
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="lab-date-input"
              className="mt-1.5 rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lab-creat">Creatinine ({creatUnit})</Label>
              <Input
                id="lab-creat"
                type="number"
                step="0.01"
                value={form.creatinine}
                onChange={(e) => setForm({ ...form, creatinine: e.target.value })}
                data-testid="lab-creatinine-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="lab-sdma">SDMA (µg/dL)</Label>
              <Input
                id="lab-sdma"
                type="number"
                step="0.1"
                value={form.sdma}
                onChange={(e) => setForm({ ...form, sdma: e.target.value })}
                data-testid="lab-sdma-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="lab-usg">USG</Label>
              <Input
                id="lab-usg"
                type="number"
                step="0.001"
                value={form.usg}
                onChange={(e) => setForm({ ...form, usg: e.target.value })}
                data-testid="lab-usg-input"
                className="mt-1.5 rounded-xl"
                placeholder="e.g. 1.028"
              />
            </div>
            <div>
              <Label htmlFor="lab-upc">UPC</Label>
              <Input
                id="lab-upc"
                type="number"
                step="0.01"
                value={form.upc}
                onChange={(e) => setForm({ ...form, upc: e.target.value })}
                data-testid="lab-upc-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="lab-bp">Systolic BP (mmHg)</Label>
              <Input
                id="lab-bp"
                type="number"
                value={form.bp}
                onChange={(e) => setForm({ ...form, bp: e.target.value })}
                data-testid="lab-bp-input"
                className="mt-1.5 rounded-xl"
              />
            </div>
          </div>

          {form.bp !== "" && (
            <div className="rounded-xl bg-secondary/60 p-4 space-y-3 border border-border/60" data-testid="bp-conditions-block">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Measurement conditions
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Method</Label>
                  <Select value={form.bp_method} onValueChange={(v) => setForm({ ...form, bp_method: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="lab-bp-method-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Doppler">Doppler</SelectItem>
                      <SelectItem value="Oscillometric">Oscillometric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Cuff position</Label>
                  <Select value={form.bp_cuff} onValueChange={(v) => setForm({ ...form, bp_cuff: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="lab-bp-cuff-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Forelimb">Forelimb</SelectItem>
                      <SelectItem value="Hindlimb">Hindlimb</SelectItem>
                      <SelectItem value="Tail">Tail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Stress level</Label>
                  <Select value={form.bp_stress} onValueChange={(v) => setForm({ ...form, bp_stress: v })}>
                    <SelectTrigger className="mt-1 rounded-xl" data-testid="lab-bp-stress-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Calm">Calm</SelectItem>
                      <SelectItem value="Anxious">Anxious</SelectItem>
                      <SelectItem value="Aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="lab-bp-n" className="text-xs">Avg of N readings</Label>
                  <Input
                    id="lab-bp-n"
                    type="number"
                    min="1"
                    value={form.bp_n}
                    onChange={(e) => setForm({ ...form, bp_n: e.target.value })}
                    data-testid="lab-bp-n-input"
                    className="mt-1 rounded-xl"
                    placeholder="e.g. 5"
                  />
                </div>
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="lab-notes">Notes</Label>
            <Textarea
              id="lab-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="lab-notes-input"
              className="mt-1.5 rounded-xl"
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} data-testid="lab-submit-button" className="rounded-full">
              {submitting ? "Saving…" : "Save result"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
